import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBase } from "../apiBase";
import {
  FaCheckCircle,
  FaTag,
  FaTimes,
  FaLock,
  FaUserFriends,
  FaChartLine,
  FaCog,
  FaChevronDown,
  FaChevronUp,
  FaCreditCard,
  FaGift,
  FaShieldAlt,
  FaRocket,
  FaHeadset,
  FaSyncAlt,
  FaStar,
  FaInfoCircle
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

export default function CheckoutModal({ plan, billingCycle, onClose, tenantId }) {
  const navigate = useNavigate();
  const [referral, setReferral] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [referralError, setReferralError] = useState("");
  const [referralSuccess, setReferralSuccess] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const safeBillingCycle = billingCycle || "monthly";

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async () => {
    setIsLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        alert("Razorpay SDK failed to load.");
        setIsLoading(false);
        return;
      }

      const res = await fetch(
        `${getApiBase()}/subscriptions/create?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            plan_code: plan.plan_code,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "Failed to create subscription");
        setIsLoading(false);
        return;
      }

      const { razorpay_subscription_id, razorpay_key_id } = data;
      const options = {
        key: razorpay_key_id,
        subscription_id: razorpay_subscription_id,
        name: "Your Company Name",
        description: `${plan.name} Subscription`,
        theme: { color: "#4A2E52" },
        handler: async function (response) {
  try {
    const verifyRes = await fetch(
      `${getApiBase()}/subscriptions/verify?tenant_id=${tenantId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_subscription_id: response.razorpay_subscription_id,
          razorpay_signature: response.razorpay_signature,
        }),
      }
    );

    if (verifyRes.ok) {
      window.dispatchEvent(new Event("subscriptionUpdated"));

      onClose(); // close checkout modal
    } else {
      alert("Payment verification failed");
    }
  } catch (err) {
    console.error(err);
    alert("Verification error");
  } finally {
    setIsLoading(false);
  }
},
      };

      const rzp = new window.Razorpay(options);
      rzp.on('close', () => setIsLoading(false));
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
      setIsLoading(false);
    }
  };

  const handleApplyReferral = async () => {
    if (!referral.trim()) {
      setReferralError("Please enter a referral code");
      return;
    }

    setIsApplying(true);
    setReferralError("");
    setReferralSuccess(false);

    try {
      const res = await fetch(`${getApiBase()}/subscriptions/redeem-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referral_code: referral.trim(),
          subscription_tier: plan.id.toLowerCase(),
          billing_cycle: safeBillingCycle,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.valid) {
        setReferralError(data.message || "Invalid referral code");
        return;
      }

      setReferralSuccess(true);
      setReferral("");
      setTimeout(() => {
        window.dispatchEvent(new Event("subscriptionUpdated"));
        onClose();
      }, 500);
    } catch (err) {
      setReferralError("Something went wrong. Please try again.");
    } finally {
      setIsApplying(false);
    }
  };

  useEffect(() => {
    if (plan.price === "Free") {
      setPaymentMethod('referral');
    }
  }, [plan]);

  const isFreePlan = plan.price === "Free";
  const yearlySavings = safeBillingCycle === "yearly" && plan.savings;

  // Get plan-specific colors
  const getPlanColors = () => {
    if (plan.isPopular) {
      return {
        primary: "#4A2E52",
        secondary: "#6F4A74",
        border: "#4A2E52",
        badge: "from-[#4A2E52] to-[#6F4A74]",
        headerBg: "from-[#FFF8E6] to-white",
        headerBorder: "border-b-2 border-[#FFE9B8]"
      };
    }
    
    switch(plan.id) {
      case 'starter':
        return {
          primary: "#FFD700",
          secondary: "#FFA500",
          border: "#FFE9B8",
          badge: "from-[#FFD700] to-[#FFA500]",
          headerBg: "from-[#FFF8E6] to-white",
          headerBorder: "border-b-2 border-[#FFE9B8]"
        };
      case 'pro':
        return {
          primary: "#4CAF50",
          secondary: "#2E7D32",
          border: "#D4EDE0",
          badge: "from-[#4CAF50] to-[#2E7D32]",
          headerBg: "from-[#E8F5F0] to-white",
          headerBorder: "border-b-2 border-[#D4EDE0]"
        };
      case 'enterprise':
        return {
          primary: "#6B7280",
          secondary: "#4B5563",
          border: "#E5E7EB",
          badge: "from-[#6B7280] to-[#4B5563]",
          headerBg: "from-[#F3F4F6] to-white",
          headerBorder: "border-b-2 border-gray-200"
        };
      default:
        return {
          primary: "#4A2E52",
          secondary: "#6F4A74",
          border: "#E9DFF0",
          badge: "from-[#4A2E52] to-[#6F4A74]",
          headerBg: "from-[#F8F5FA] to-white",
          headerBorder: "border-b-2 border-[#E9DFF0]"
        };
    }
  };

  const planColors = getPlanColors();

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white w-full max-w-5xl rounded-3xl shadow-2xl p-6 max-h-[95vh] overflow-y-auto hide-scrollbar"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-6 text-gray-400 hover:text-gray-700 text-xl z-10"
        >
          ✕
        </button>

        <div className="bg-gradient-to-b from-white to-[#FAF7FB] px-4 py-6 text-[#4A2E52]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Plan Details */}
            <div className="lg:col-span-2 space-y-6">
              <div 
                className={`bg-gradient-to-b ${planColors.headerBg} rounded-3xl p-6 ${planColors.headerBorder} relative overflow-hidden`}
              >
                {/* Decorative accent */}
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#4A2E52]/5 rounded-full blur-3xl"></div>
                
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 relative z-10">
                  <div>
                    <div className="inline-flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-[#E9DFF0] text-[#4A2E52] text-xs font-semibold rounded-full">
                        SELECTED PLAN
                      </span>
                      {plan.isPopular && (
                        <span className="px-3 py-1 bg-gradient-to-r from-[#4A2E52] to-[#6F4A74] text-white text-xs font-semibold rounded-full flex items-center gap-1">
                          <FaStar className="text-[10px]" /> MOST POPULAR
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      {plan.name}
                      {plan.id === "pro" && <FaRocket className="text-[#4CAF50]" />}
                    </h2>
                    <p className="text-gray-600 mt-1">{plan.tagline}</p>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-baseline gap-2 justify-end">
                      <span className="text-3xl font-extrabold text-[#111827]">{plan.price}</span>
                      {!isFreePlan && (
                        <span className="text-gray-500">
                          /{safeBillingCycle === "yearly" ? "year" : "month"}
                        </span>
                      )}
                    </div>
                    {yearlySavings && (
                      <div className="mt-2 inline-block bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                        💰 {plan.savings}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center justify-between w-full text-sm font-semibold text-[#4A2E52] pb-2"
                  >
                    <span>What you'll get</span>
                    {showDetails ? <FaChevronUp /> : <FaChevronDown />}
                  </button>

                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          {plan.features.map((feature, i) => (
                            <div key={i} className="flex items-start gap-3">
                              {feature.included ? (
                                <div className="mt-0.5 w-5 h-5 rounded-full bg-[#4CAF50] flex items-center justify-center">
                                  <FaCheckCircle className="text-white text-[10px]" />
                                </div>
                              ) : (
                                <div className="mt-0.5 w-5 h-5 rounded-full border-2 border-dashed border-gray-300" />
                              )}
                              <span className={feature.included ? "font-medium text-gray-800" : "text-gray-400 line-through"}>
                                {feature.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-2xl border border-gray-200 text-center shadow-sm">
                  <FaShieldAlt className="text-[#4A2E52] mx-auto text-xl mb-2" />
                  <p className="text-xs font-medium">Secure Payments</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-gray-200 text-center shadow-sm">
                  <FaSyncAlt className="text-[#4A2E52] mx-auto text-xl mb-2" />
                  <p className="text-xs font-medium">Instant Activation</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-gray-200 text-center shadow-sm">
                  <FaHeadset className="text-[#4A2E52] mx-auto text-xl mb-2" />
                  <p className="text-xs font-medium">Priority Support</p>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-gray-200 text-center shadow-sm">
                  <FaRocket className="text-[#4A2E52] mx-auto text-xl mb-2" />
                  <p className="text-xs font-medium">Cancel Anytime</p>
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sticky top-8">
                <h3 className="text-lg font-bold mb-5 flex items-center gap-2">
                  <FaCreditCard className="text-[#4A2E52]" /> 
                  Payment Details
                </h3>

                <div className="space-y-4 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-500">Plan</span>
                    <span className="font-semibold">{plan.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-500">Billing cycle</span>
                    <span className="font-semibold capitalize">{safeBillingCycle}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-500">Price</span>
                    <span className="font-semibold">
                      {plan.price}
                      {!isFreePlan && (
                        <span className="text-gray-400 text-xs ml-1">
                          {safeBillingCycle === "yearly" ? "/year" : "/month"}
                        </span>
                      )}
                    </span>
                  </div>
                  {yearlySavings && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-500">You save</span>
                      <span className="text-green-600 font-semibold">{plan.savings}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 font-bold text-lg">
                    <span>Total</span>
                    <span>{isFreePlan ? "Free" : plan.price}</span>
                  </div>
                </div>

                {/* Payment Method Selection */}
                {!paymentMethod && !isFreePlan && (
                  <div className="mt-6 space-y-4">
                    <h4 className="font-semibold text-gray-700">Select Payment Method</h4>
                    <button
                      onClick={() => setPaymentMethod('razorpay')}
                      className={`w-full flex items-center justify-between gap-4 p-4 border-2 rounded-2xl transition ${
                        paymentMethod === "razorpay"
                          ? "border-[#4A2E52] bg-[#4A2E52]/5"
                          : "border-gray-200 hover:border-[#4A2E52] hover:bg-[#4A2E52]/5"
                      }`}
                     >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#4A2E52]/10 flex items-center justify-center">
                          <FaCreditCard className="text-[#4A2E52] text-xl" />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-[#4A2E52]">Razorpay</div>
                          <div className="text-xs text-gray-500">Credit/Debit Card, UPI, Net Banking</div>
                        </div>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === "razorpay"
                            ? "border-[#4A2E52]"
                            : "border-gray-300"
                        }`}
                      >
                        {paymentMethod === "razorpay" && (
                          <div className="w-3 h-3 rounded-full bg-[#4A2E52]" />
                        )}
                      </div>
                    </button>

                    <button
                      onClick={() => setPaymentMethod("referral")}
                      className={`w-full flex items-center justify-between gap-4 p-4 border-2 rounded-2xl transition ${
                        paymentMethod === "referral"
                          ? "border-purple-600 bg-purple-50"
                          : "border-gray-200 hover:border-purple-400 hover:bg-purple-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                          <FaGift className="text-purple-600 text-xl" />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-purple-600">Referral Code</div>
                          <div className="text-xs text-gray-500">
                            Activate with valid code
                          </div>
                        </div>
                      </div>

                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === "referral"
                            ? "border-purple-600"
                            : "border-gray-300"
                        }`}
                      >
                        {paymentMethod === "referral" && (
                          <div className="w-3 h-3 rounded-full bg-purple-600" />
                        )}
                      </div>
                    </button>
                  </div>
                )}

                {/* Razorpay Payment */}
                {paymentMethod === 'razorpay' && (
                  <div className="mt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-5">
                      <div className="flex items-start gap-3">
                        <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-blue-800 font-medium">Secure Payment Gateway</p>
                          <p className="text-xs text-blue-600 mt-1">
                            You'll be redirected to Razorpay's payment page
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleCheckout}
                      disabled={isLoading}
                      className="w-full py-3.5 rounded-2xl font-semibold bg-gradient-to-r from-[#4A2E52] to-[#6F4A74] text-white hover:opacity-95 transition flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                          />
                          Processing...
                        </>
                      ) : (
                        "Proceed to Payment"
                      )}
                    </button>

                    <button
                      onClick={() => setPaymentMethod(null)}
                      className="mt-3 w-full text-sm text-gray-500 hover:text-[#4A2E52] font-medium"
                    >
                      ← Change payment method
                    </button>
                  </div>
                )}

                {/* Referral Payment */}
                {(paymentMethod === 'referral' || isFreePlan) && (
                  <div className="mt-6">
                    <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-5">
                      <div className="flex items-start gap-3">
                        <FaGift className="text-purple-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-purple-800 font-medium">Referral Program</p>
                          <p className="text-xs text-purple-600 mt-1">
                            Enter your referral code to activate this plan
                          </p>
                        </div>
                      </div>
                    </div>

                    <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2">
                      <FaTag /> Referral code
                    </label>

                    <div className="flex">
                      <input
                        value={referral}
                        onChange={(e) => {
                          setReferral(e.target.value);
                          setReferralError("");
                        }}
                        disabled={isApplying || referralSuccess}
                        className={`flex-1 px-4 py-3 text-sm rounded-l-2xl border ${
                          referralError ? "border-red-300" : "border-gray-300"
                        } focus:outline-none focus:ring-2 focus:ring-[#4A2E52]/30`}
                        placeholder="Enter referral code"
                      />
                      <button
                        onClick={handleApplyReferral}
                        disabled={isApplying || referralSuccess}
                        className="px-5 rounded-r-2xl bg-[#4A2E52] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#3A2340] transition"
                      >
                        {isApplying ? "Applying..." : referralSuccess ? "Applied" : "Apply"}
                      </button>
                    </div>

                    {referralError && <p className="mt-2 text-xs text-red-500">{referralError}</p>}
                    {referralSuccess && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 p-3 bg-green-100 text-green-800 text-sm font-medium rounded-2xl"
                      >
                        ✔ Referral applied successfully! Subscription activated.
                      </motion.div>
                    )}

                    {!isFreePlan && (
                      <button
                        onClick={() => setPaymentMethod(null)}
                        className="mt-4 w-full text-sm text-gray-500 hover:text-[#4A2E52] font-medium"
                      >
                        ← Change payment method
                      </button>
                    )}
                  </div>
                )}

                {/* Security Notice */}
                <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <FaLock className="text-green-500" />
                  <span>Secure & encrypted payment processing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}